/**
 * Dropdown - A molecule component for dropdown menus.
 *
 * Manages open/close state and click-outside detection.
 * Provides positioning and z-index management for dropdown content.
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@lean-agility/design-system/react';

export interface DropdownProps {
  /**
   * The trigger element that opens/closes the dropdown.
   */
  trigger: ReactNode;
  /**
   * The dropdown content to display when open.
   */
  children: ReactNode;
  /**
   * Whether the dropdown is controlled externally.
   */
  controlled?: boolean;
  /**
   * Controlled open state (when controlled is true).
   */
  isOpen?: boolean;
  /**
   * Callback when open state changes (when controlled is true).
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Additional CSS classes for the container.
   */
  className?: string;
  /**
   * Alignment of the dropdown content relative to trigger.
   */
  align?: 'left' | 'right';
}

export function Dropdown({
  trigger,
  children,
  controlled = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  className,
  align = 'right',
}: DropdownProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isOpen = controlled ? (controlledIsOpen ?? false) : internalIsOpen;
  const setIsOpen = controlled && onOpenChange ? onOpenChange : setInternalIsOpen;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const handleTriggerClick = () => {
    if (controlled && onOpenChange) {
      onOpenChange(!isOpen);
    } else if (!controlled) {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <div onClick={handleTriggerClick}>{trigger}</div>
      {isOpen && <div className={cn('absolute z-50 mt-2', align === 'right' ? `right-0` : `left-0`)}>{children}</div>}
    </div>
  );
}
